import { W3CTraceContextPropagator } from '@opentelemetry/core';
import { OTLPTraceExporter } from '@opentelemetry/exporter-trace-otlp-http';
import { registerInstrumentations } from '@opentelemetry/instrumentation';
import { FetchInstrumentation } from '@opentelemetry/instrumentation-fetch';
import { Resource } from '@opentelemetry/resources';
import {
  BatchSpanProcessor,
  WebTracerProvider
} from '@opentelemetry/sdk-trace-web';
import { SEMRESATTRS_SERVICE_NAME } from '@opentelemetry/semantic-conventions';

const supportedGeos = ['uk', 'us'];

/**
 * Initialise OpenTelemetry instrumentation for fetch requests
 *
 * @param {string} service - Name of the service
 * @param {string} collector - Base URL of the collector to send traces to e.g. https://collector.example.com
 * @param {string} geo - Geo location of the service [uk, us]
 * @param {(string[]|RegExp[])} [propagate] - List of URLs to propagate traceparent header to, requires opt-in to avoid CORS issues
 * @param {(string[]|RegExp[])} [ignore] - List of URLs to ignore for fetch instrumentation
 * @returns
 */
export function initOpenTelemetry(
  service,
  collector,
  geo,
  propagate = [],
  ignore = []
) {
  if (collector === undefined) {
    console.warn('Not initialising OpenTelemetry: collector url undefined');
    return;
  }

  if (supportedGeos.indexOf(geo) === -1) {
    console.warn(`Not initialising OpenTelemetry: geo=${geo} not supported`);
    return;
  }

  // Configure the trace provider and resource
  const resource = new Resource({ [SEMRESATTRS_SERVICE_NAME]: service });
  const provider = new WebTracerProvider({ resource });

  // Add a batch processor that exports to our public HTTP collector
  const exporter = new OTLPTraceExporter({
    url: `${collector}/${geo}/v1/traces`,
    headers: {} // Forces sending via xhr/fetch, see: https://github.com/open-telemetry/opentelemetry-js/issues/3062#issuecomment-1189189494
  });
  provider.addSpanProcessor(
    new BatchSpanProcessor(exporter, {
      maxQueueSize: 100,
      maxExportBatchSize: 50,
      scheduledDelayMillis: 3000,
      exportTimeoutMillis: 10000
    })
  );

  // Register an async context manager and the trace context propagator
  provider.register({
    propagator: new W3CTraceContextPropagator()
  });

  // Register instrumentations for fetch requests including a custom attribute
  // that contains the URL the request was made from
  registerInstrumentations({
    instrumentations: [
      new FetchInstrumentation({
        propagateTraceHeaderCorsUrls: propagate,
        clearTimingResources: true,
        ignoreUrls: ignore,
        ignoreNetworkEvents: true,
        applyCustomAttributesOnSpan(span) {
          span.setAttribute('http.referrer', window.location.href);
        }
      })
    ]
  });
}
